import { useState, useCallback, useEffect } from "react";
import { toast } from "react-toastify";
import apiClient from "utils/apiClient";
import { PROFILE_SERVICE_BASE_URL } from "constants";

const BASE_URL = `${PROFILE_SERVICE_BASE_URL}/api/v1/help`;
const DEFAULT_PAGE = 0;
const DEFAULT_SIZE = 20;

export default function useHelpRequests() {
  const authToken = sessionStorage.getItem("authToken");

  const [helpRequests, setHelpRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [error, setError] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);

  // error handler
  const handleAxiosErrors = (err, action = "operation") => {
    let message = "An unexpected error occurred.";

    if (err.response) {
      const status = err.response.status;

      if (status >= 500) {
        message = "Server error. Please try again later.";
      } else {
        message =
          err.response.data?.message ||
          `Failed to ${action}: ${err.response.statusText}`;
      }
    } else if (err.request) {
      message = "No response from server. Check your connection.";
    } else {
      message = err.message || message;
    }

    toast.error(message);
    console.error(`${action} error:`, err);
    setError(message);
  };

  // normalize data
  const formatHelpRequest = (data) => ({
    id: data.id,
    category: data.category,
    message: data.message,
    status: data.status,
    sourceApp: data.sourceApp,
    user: data.userProfile,
    name: data.name,
    email: data.email,
    phone: data.phone,
    createdAt: data.createdAt,
  });

  const handlePaginatedResponse = (res, page = 0) => {
    const { content = [], totalPages = 1, totalElements = 0 } = res.data;

    setHelpRequests((prev) =>
      page === 0
        ? content.map(formatHelpRequest)
        : [...prev, ...content.map(formatHelpRequest)],
    );
    setTotalPages(totalPages);
    setTotalElements(totalElements);
  };

  // get by status
  const fetchByStatus = useCallback(
    async (status = "OPEN", page = DEFAULT_PAGE, size = DEFAULT_SIZE) => {
      if (!authToken) return;

      setLoading(true);
      setError(null);

      try {
        const params = { page, size };
        const res = await apiClient.get(`${BASE_URL}/status/${status}`, {
          params,
        });

        handlePaginatedResponse(res);
      } catch (err) {
        handleAxiosErrors(err, "fetch help requests by status");
      } finally {
        setLoading(false);
        setInitialLoad(false);
      }
    },
    [authToken],
  );

  // search
  const searchHelpRequests = useCallback(
    async ({
      category,
      keyword,
      status,
      sourceApp,
      page = DEFAULT_PAGE,
      size = DEFAULT_SIZE,
    } = {}) => {
      if (!authToken) return;

      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams({
          page: String(page),
          size: String(size),
        });

        if (category) params.append("category", category);
        if (keyword) params.append("keyword", keyword);
        if (status) params.append("status", status);
        if (sourceApp) params.append("sourceApp", sourceApp);

        const res = await apiClient.get(
          `${BASE_URL}/search?${params.toString()}`,
        );

        handlePaginatedResponse(res, page);
      } catch (err) {
        handleAxiosErrors(err, "search help requests");
      } finally {
        setLoading(false);
      }
    },
    [authToken],
  );

  // get by id
  const getHelpRequestById = useCallback(
    async (id) => {
      if (!authToken || !id) return;

      try {
        const res = await apiClient.get(`${BASE_URL}/${id}`);
        return formatHelpRequest(res.data);
      } catch (err) {
        handleAxiosErrors(err, "fetch help request by id");
      }
    },
    [authToken],
  );

  // update status
  const updateStatus = useCallback(
    async (id, status) => {
      if (!authToken || !id || !status) return;

      const previous = [...helpRequests];

      // optimistic update
      setHelpRequests((prev) =>
        prev.map((item) => (item.id === id ? { ...item, status } : item)),
      );

      try {
        await apiClient.patch(`${BASE_URL}/${id}/status`, null, {
          params: { status },
        });

        toast.success("Status updated successfully");
      } catch (err) {
        setHelpRequests(previous);
        handleAxiosErrors(err, "update help request status");
      }
    },
    [authToken, helpRequests],
  );

  // get all
  const fetchAll = useCallback(
    async (page = DEFAULT_PAGE, size = DEFAULT_SIZE) => {
      if (!authToken) return;

      setLoading(true);
      setError(null);

      try {
        const params = { page, size };
        const res = await apiClient.get(BASE_URL, { params });
        handlePaginatedResponse(res);
      } catch (err) {
        handleAxiosErrors(err, "fetch all help requests");
      } finally {
        setLoading(false);
        setInitialLoad(false);
      }
    },
    [authToken],
  );

  useEffect(() => {
    if (authToken) fetchAll();
  }, [authToken, fetchAll]);

  return {
    helpRequests,
    loading,
    error,
    initialLoad,
    totalPages,
    totalElements,
    fetchAll,
    fetchByStatus,
    searchHelpRequests,
    getHelpRequestById,
    updateStatus,
  };
}
